import { Checkbox, Fade, TableCell, TableRow } from '@mui/material';
import NombreEditableInput from '@/components/NombreEditableInput';
import dayjs from 'dayjs';
import { Fragment, memo } from 'react';
import { useActualizarNombreCarritoMutation } from '@/redux/api/informeApi';
import { ICarrito } from '@/redux/models';
import { formatToCurrencyARS } from '@/utils/allUtils';
import { useTablaCarritoV2Props, ViewAs } from '../../logics/useTablaCarritoV2';
import CarpetaActionButtons from './CarpetaActionButtons';
import TableCarpetaEstados from './TableCarpetaEstados';
import TableRowInformes from './TableRowInformes';
import UserMemo from './UserMemo';

interface TableRowComponentProps {
    carpeta: ICarrito;
    index: number;
    isSelected: boolean;
    isInformesOpen: boolean;
    handleOpenRow: (id: number) => void;
    context: useTablaCarritoV2Props
    rowsOpen: number[]
}

const MemoizedCells = memo(({ carpeta, setCarpeta, viewAs, hayRechazados, setModal }: { carpeta: ICarrito, setCarpeta: (carpeta: ICarrito) => void, viewAs: useTablaCarritoV2Props['viewAs'], hayRechazados: boolean, setModal: useTablaCarritoV2Props['setModal'] }) => {
    const [actualizarEntrada] = useActualizarNombreCarritoMutation();

    const handleActualizarNombre = async (nombre: string) => {
        await actualizarEntrada({ id: carpeta.idCarrito, nombre });
        setCarpeta({ ...carpeta, nombreCarrito: nombre });
    }

    return (
        <>
            <TableCell component="th" scope="row" padding="none">
                {carpeta.idCarrito}
            </TableCell>
            <TableCell component="th" scope="row" padding="none">
                {viewAs === ViewAs.USER ? carpeta.nombreCarrito :
                    <NombreEditableInput
                        label='Nombre de la carpeta'
                        editable={true} nombre={carpeta.nombreCarrito}
                        onPressSave={handleActualizarNombre} />
                }
            </TableCell>
            <TableCell sx={{ pl: '1rem' }} component="th" scope="row" padding="none">
                {dayjs(carpeta.fechaDeCreacionCarrito).format('DD/MM/YYYY HH:mm')}
            </TableCell>
            <TableCell sx={{ pl: '1rem' }} component="th" scope="row" padding="none">
                {formatToCurrencyARS(carpeta.montoTotal)}
            </TableCell>
            {ViewAs.ADMIN === viewAs &&
                <TableCell component="th" scope="row" padding="none">
                    <UserMemo user={carpeta.usuario} />
                </TableCell>
            }
            <TableCell align="center" aria-label='estados-de-carpeta'>
                <TableCarpetaEstados carpeta={carpeta} hayRechazados={hayRechazados} setModal={setModal} viewAs={viewAs} />
            </TableCell>

        </>
    );
}, (prevProps, nextProps) => {
    return prevProps.carpeta.nombreCarrito === nextProps.carpeta.nombreCarrito &&
        prevProps.carpeta.fechaDeCreacionCarrito === nextProps.carpeta.fechaDeCreacionCarrito &&
        prevProps.carpeta.montoTotal === nextProps.carpeta.montoTotal &&
        prevProps.carpeta.pagado === nextProps.carpeta.pagado &&
        prevProps.carpeta.liquidado === nextProps.carpeta.liquidado &&
        prevProps.carpeta.pdfLiquidacion === nextProps.carpeta.pdfLiquidacion &&
        prevProps.carpeta.observed === nextProps.carpeta.observed &&
        prevProps.carpeta.estadoGeneral === nextProps.carpeta.estadoGeneral &&
        prevProps.carpeta.facturaAdjunta === nextProps.carpeta.facturaAdjunta &&
        prevProps.carpeta.usuario === nextProps.carpeta.usuario
});

const TableRowComponent = ({ carpeta, index, isSelected, isInformesOpen, handleOpenRow, context }: TableRowComponentProps) => {
    const { toggleSelectedCarrito, setModal, informes, viewAs, carpetasBeingDeleted } = context;
    const misInformes = informes[carpeta.idCarrito] || [];
    const hayRechazados = misInformes.some((informe) => informe.rejected);
    const isBeingDeleted = carpetasBeingDeleted.includes(carpeta.idCarrito);

    return (
        <Fragment key={`fragment${carpeta.idCarrito}`}>

            {/* Tabla de Carpetas */}
            <Fade
                in={!isBeingDeleted}
                easing='ease'
                timeout={{ enter: 500, exit: 600 }}
                style={{
                    transitionDelay: isBeingDeleted ? '0ms' : `${index * 40}ms`,
                    transformOrigin: 'center',
                }}
            >
                <TableRow
                    role="checkbox"
                    aria-checked={isSelected}
                    tabIndex={-1}
                    selected={isSelected}
                    className={isInformesOpen ? 'bg-color-change' : 'bg-color-reset'}
                    sx={(theme) => ({
                        backgroundColor: misInformes.every((informe) => informe.printed) ? theme.palette.mode === 'light' ? '#c0f5c3 !important' : '#2d352d !important' : 'inherit',
                        opacity: isBeingDeleted ? 0 : 1,
                        transform: isBeingDeleted ? 'translateX(-100%) scale(0.8)' : 'translateX(0) scale(1)',
                        transition: 'all 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        pointerEvents: isBeingDeleted ? 'none' : 'auto'
                    })}
                >
                    <TableCell padding="checkbox">
                        {context.viewAs === ViewAs.ADMIN &&
                            <Checkbox
                                color="primary"
                                checked={isSelected}
                                onClick={() => toggleSelectedCarrito(carpeta)}
                            />
                        }
                    </TableCell>

                    <MemoizedCells
                        hayRechazados={hayRechazados}
                        carpeta={carpeta}
                        viewAs={viewAs}
                        setCarpeta={context.modificarCarpeta}
                        setModal={setModal}

                    />
                    <TableCell align="center">
                        <CarpetaActionButtons
                            viewAs={viewAs}
                            setModal={setModal}
                            handleOpenRow={handleOpenRow}
                            carpeta={carpeta}
                        />
                    </TableCell>

                </TableRow>
            </Fade>

            {/* Subtabla: Tabla de informes */}
            <TableRowInformes
                informes={misInformes}
                carpeta={carpeta}
                isOpen={isInformesOpen}
                context={context}
            />
        </Fragment>
    );
};

export default memo(TableRowComponent, (prevProps, nextProps) => {
    return prevProps.isSelected === nextProps.isSelected &&
        prevProps.isInformesOpen === nextProps.isInformesOpen &&
        prevProps.carpeta === nextProps.carpeta &&
        prevProps.context.selectedCarritos === nextProps.context.selectedCarritos &&
        prevProps.context.setModal === nextProps.context.setModal &&
        prevProps.rowsOpen === nextProps.rowsOpen;
});
